package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppBadge
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppTooltipText
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.core.util.WeekRange
import br.com.vvrefeicoes.portal.domain.model.EntryRow
import br.com.vvrefeicoes.portal.domain.model.EntryRowLabel
import br.com.vvrefeicoes.portal.domain.model.EntryTable
import com.composables.icons.lucide.ChevronDown
import com.composables.icons.lucide.ChevronUp
import com.composables.icons.lucide.EllipsisVertical
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Pencil
import com.composables.icons.lucide.Plus
import com.composables.icons.lucide.Trash2
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_add_supplier
import portalvv.shared.generated.resources.operational_col_line
import portalvv.shared.generated.resources.operational_col_total
import portalvv.shared.generated.resources.operational_supplier_edit
import portalvv.shared.generated.resources.operational_supplier_empty
import portalvv.shared.generated.resources.operational_supplier_installment_badge
import portalvv.shared.generated.resources.operational_supplier_remove
import portalvv.shared.generated.resources.operational_total_label
import portalvv.shared.generated.resources.operational_week_format

private const val WEIGHT_LABEL = 2.2f
private const val WEIGHT_WEEK = 1f
private const val WEIGHT_TOTAL = 1.3f
private val ACTION_WIDTH = 32.dp

@Composable
fun OperationalEntryTable(
    table: EntryTable,
    weeks: List<WeekRange>,
    editing: Boolean,
    onWeekChange: (rowId: String, weekIndex: Int, cents: Long?) -> Unit,
    modifier: Modifier = Modifier,
    collapsible: Boolean = false,
    initiallyExpanded: Boolean = true,
    onAddSupplier: (() -> Unit)? = null,
    onEditSupplier: ((EntryRow) -> Unit)? = null,
    onRemoveSupplier: ((EntryRow) -> Unit)? = null,
    onFieldBlur: (() -> Unit)? = null,
) {
    var expanded by remember { mutableStateOf(initiallyExpanded) }
    val showActions = onEditSupplier != null || onRemoveSupplier != null
    val showContent = !collapsible || expanded

    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            TableHeader(
                title = stringResource(table.title),
                totalCents = table.totalCents,
                collapsible = collapsible,
                expanded = expanded,
                onToggle = { expanded = !expanded },
                onAddSupplier = onAddSupplier,
            )
            if (showContent) {
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                when {
                    table.rows.isEmpty() && collapsible ->
                        Text(
                            text = stringResource(Res.string.operational_supplier_empty),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space16),
                        )
                    else -> {
                        Header(weeks, showActions)
                        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                        table.rows.forEachIndexed { index, row ->
                            EntryRowItem(
                                row = row,
                                editing = editing,
                                showActions = showActions,
                                weekCount = weeks.size,
                                onWeekChange = { weekIndex, cents -> onWeekChange(row.id, weekIndex, cents) },
                                onEdit = onEditSupplier?.let { action -> { action(row) } },
                                onRemove = onRemoveSupplier?.let { action -> { action(row) } },
                                onFieldBlur = onFieldBlur,
                            )
                            if (index < table.rows.lastIndex) {
                                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                            }
                        }
                    }
                }
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                TotalRow(totalCents = table.totalCents, weeks = weeks.size, showActions = showActions)
            }
        }
    }
}

@Composable
private fun TableHeader(
    title: String,
    totalCents: Long,
    collapsible: Boolean,
    expanded: Boolean,
    onToggle: () -> Unit,
    onAddSupplier: (() -> Unit)?,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        Row(
            modifier =
                Modifier
                    .weight(1f)
                    .then(if (collapsible) Modifier.clickable(onClick = onToggle) else Modifier),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        ) {
            if (collapsible) {
                Icon(
                    imageVector = if (expanded) Lucide.ChevronUp else Lucide.ChevronDown,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
            )
            if (collapsible && !expanded) {
                Text(
                    text = "R$ ${MoneyFormatter.formatCents(totalCents)}",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
        if (onAddSupplier != null) {
            AppButton(
                text = stringResource(Res.string.operational_add_supplier),
                onClick = onAddSupplier,
                variant = PortalButtonVariant.Text,
                leadingIcon = Lucide.Plus,
            )
        }
    }
}

@Composable
private fun Header(
    weeks: List<WeekRange>,
    showActions: Boolean,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        Text(
            text = stringResource(Res.string.operational_col_line),
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.weight(WEIGHT_LABEL),
        )
        weeks.forEachIndexed { index, range ->
            Column(
                modifier = Modifier.weight(WEIGHT_WEEK),
                horizontalAlignment = Alignment.End,
            ) {
                Text(
                    text = stringResource(Res.string.operational_week_format, index + 1),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.End,
                )
                Text(
                    text = range.label,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.End,
                )
            }
        }
        Text(
            text = stringResource(Res.string.operational_col_total),
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            textAlign = TextAlign.End,
            modifier = Modifier.weight(WEIGHT_TOTAL),
        )
        if (showActions) {
            Spacer(Modifier.width(ACTION_WIDTH))
        }
    }
}

@Composable
private fun EntryRowItem(
    row: EntryRow,
    editing: Boolean,
    showActions: Boolean,
    weekCount: Int,
    onWeekChange: (weekIndex: Int, cents: Long?) -> Unit,
    onEdit: (() -> Unit)?,
    onRemove: (() -> Unit)?,
    onFieldBlur: (() -> Unit)?,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        Row(
            modifier = Modifier.weight(WEIGHT_LABEL),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4),
        ) {
            AppTooltipText(
                text = rowTitle(row),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier.weight(1f, fill = false),
            )
            row.installment?.let { installment ->
                AppBadge(
                    text = stringResource(Res.string.operational_supplier_installment_badge, installment.current, installment.total),
                    containerColor = AppPalette.Info100,
                    contentColor = AppPalette.Info900,
                )
            }
        }
        (0 until weekCount).forEach { weekIndex ->
            CurrencyCell(
                rowId = row.id,
                weekIndex = weekIndex,
                cents = row.weeks.getOrNull(weekIndex),
                editing = editing,
                onChange = { onWeekChange(weekIndex, it) },
                modifier = Modifier.weight(WEIGHT_WEEK),
                onBlur = onFieldBlur,
            )
        }
        TotalCell(MoneyFormatter.formatCents(row.totalCents), WEIGHT_TOTAL)
        if (showActions) {
            RowActions(onEdit = onEdit, onRemove = onRemove)
        }
    }
}

@Composable
private fun RowActions(
    onEdit: (() -> Unit)?,
    onRemove: (() -> Unit)?,
) {
    Box(modifier = Modifier.width(ACTION_WIDTH)) {
        var expanded by remember { mutableStateOf(false) }
        IconButton(
            onClick = { expanded = true },
            modifier = Modifier.size(28.dp),
        ) {
            Icon(
                imageVector = Lucide.EllipsisVertical,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            if (onEdit != null) {
                DropdownMenuItem(
                    text = { Text(stringResource(Res.string.operational_supplier_edit)) },
                    leadingIcon = { Icon(Lucide.Pencil, contentDescription = null) },
                    onClick = {
                        expanded = false
                        onEdit()
                    },
                )
            }
            if (onRemove != null) {
                DropdownMenuItem(
                    text = { Text(stringResource(Res.string.operational_supplier_remove)) },
                    leadingIcon = { Icon(Lucide.Trash2, contentDescription = null) },
                    onClick = {
                        expanded = false
                        onRemove()
                    },
                )
            }
        }
    }
}

@Composable
private fun TotalRow(
    totalCents: Long,
    weeks: Int,
    showActions: Boolean,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        Text(
            text = stringResource(Res.string.operational_total_label),
            style = MaterialTheme.typography.labelLarge,
            modifier = Modifier.weight(WEIGHT_LABEL),
        )
        repeat(weeks) {
            Spacer(Modifier.weight(WEIGHT_WEEK))
        }
        TotalCell(MoneyFormatter.formatCents(totalCents), WEIGHT_TOTAL)
        if (showActions) {
            Spacer(Modifier.width(ACTION_WIDTH))
        }
    }
}

@Composable
private fun RowScope.TotalCell(
    text: String,
    weight: Float,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelLarge,
        color = MaterialTheme.colorScheme.onSurface,
        textAlign = TextAlign.End,
        maxLines = 1,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun rowTitle(row: EntryRow): String =
    when (val label = row.label) {
        is EntryRowLabel.Resource -> stringResource(label.value)
        is EntryRowLabel.Supplier -> label.value
    }

@Composable
private fun CurrencyCell(
    rowId: String,
    weekIndex: Int,
    cents: Long?,
    editing: Boolean,
    onChange: (Long?) -> Unit,
    modifier: Modifier = Modifier,
    onBlur: (() -> Unit)? = null,
) {
    if (!editing) {
        Text(
            text = MoneyFormatter.formatCents(cents).ifEmpty { "0,00" },
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurface,
            textAlign = TextAlign.End,
            maxLines = 1,
            modifier = modifier,
        )
        return
    }
    val interactionSource = remember { MutableInteractionSource() }
    val focused by interactionSource.collectIsFocusedAsState()
    var text by remember(rowId, weekIndex) { mutableStateOf(MoneyFormatter.formatCents(cents)) }
    var wasFocused by remember(rowId, weekIndex) { mutableStateOf(false) }
    LaunchedEffect(focused) {
        if (focused) {
            wasFocused = true
        } else if (wasFocused) {
            text = MoneyFormatter.formatCents(MoneyFormatter.parseReaisToCents(text))
            onBlur?.invoke()
        }
    }
    LaunchedEffect(cents) {
        if (!focused) {
            val formatted = MoneyFormatter.formatCents(cents)
            if (text != formatted) text = formatted
        }
    }
    Surface(
        modifier = modifier,
        shape = AppShapes.small,
        color = MaterialTheme.colorScheme.surface,
        border =
            BorderStroke(
                width = 1.dp,
                color = if (focused) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outline,
            ),
    ) {
        BasicTextField(
            value = text,
            onValueChange = { new ->
                text = new
                onChange(MoneyFormatter.parseReaisToCents(new))
            },
            singleLine = true,
            interactionSource = interactionSource,
            textStyle =
                MaterialTheme.typography.bodyMedium.copy(
                    color = MaterialTheme.colorScheme.onSurface,
                    textAlign = TextAlign.End,
                ),
            cursorBrush = SolidColor(MaterialTheme.colorScheme.primary),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space4),
            decorationBox = { inner ->
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.CenterEnd,
                ) {
                    if (text.isEmpty()) {
                        Text(
                            text = "0,00",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.5f),
                            textAlign = TextAlign.End,
                        )
                    }
                    inner()
                }
            },
        )
    }
}
